// Thin, optional RPC transport for the server-authoritative milestone.
// The local build deliberately works without credentials; when a host injects
// VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, callers can send intent only.
export class SafApi {
  constructor({ url, anonKey, accessToken = null, auth = null } = {}) {
    this.url = url?.replace(/\/$/, '') || '';
    this.anonKey = anonKey || '';
    this._accessToken = accessToken;
    this.auth = auth;
  }

  get configured() { return Boolean(this.url && this.anonKey); }
  get accessToken() { return this.auth?.accessToken || this._accessToken; }
  set accessToken(value) { this._accessToken = value; }

  async rpc(name, args = {}) {
    if (!this.configured) return { data: null, error: new Error('Cloud authority is not configured.') };
    try {
      const response = await fetch(`${this.url}/rest/v1/rpc/${name}`, {
        method: 'POST',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken || this.anonKey}`,
          'Content-Type': 'application/json',
          Prefer: 'return=representation',
        },
        body: JSON.stringify(args),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) return { data: null, error: new Error(payload?.message || `RPC ${name} failed (${response.status})`) };
      return { data: payload, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  state() { return this.rpc('saf_state'); }
  enlist(vocation) { return this.rpc('saf_enlist', { p_vocation: vocation }); }
  buyUnit(tier, count = 1) { return this.rpc('saf_buy_unit', { p_tier: tier, p_count: count }); }
  buyUpgrade(upgradeId) { return this.rpc('saf_buy_upgrade', { p_upgrade_id: upgradeId }); }
  setPosture(allocation) { return this.rpc('saf_set_posture', { p_alloc: allocation }); }
  claimCommand(index) { return this.rpc('saf_claim_command', { p_order_index: index }); }
  setIppt(grade) { return this.rpc('saf_set_ippt', { p_grade: grade }); }
  ord() { return this.rpc('saf_ord'); }
  releaseBreath() { return this.rpc('saf_release_breath'); }
  preemptThreat(threatId) { return this.rpc('saf_preempt_threat', { p_threat_id: threatId }); }
  setAutomation(book, respond) { return this.rpc('saf_set_automation', { p_book: book, p_respond: respond }); }
  buyTempo() { return this.rpc('saf_buy_tempo'); }
  startChallenge(id) { return this.rpc('saf_start_challenge', { p_id: id }); }
  abandonChallenge() { return this.rpc('saf_abandon_challenge'); }
  commission() { return this.rpc('saf_commission'); }
  buyCommissionUpgrade(id) { return this.rpc('saf_buy_commission_upgrade', { p_id: id }); }
  syncAchievements(ids) { return this.rpc('saf_sync_achievements', { p_ids: ids }); }
  buyManager(tier) { return this.rpc('saf_buy_manager', { p_tier: tier }); }
  marketBuy(lots) { return this.rpc('saf_market_buy', { p_lots: lots }); }
  marketSell(lots) { return this.rpc('saf_market_sell', { p_lots: lots }); }
}

export function cloudApi(auth = null) {
  const url = import.meta.env.VITE_SUPABASE_URL;
  let accessToken = null;
  try {
    const projectRef = url ? new URL(url).hostname.split('.')[0] : '';
    const session = projectRef ? JSON.parse(localStorage.getItem(`sb-${projectRef}-auth-token`) || 'null') : null;
    accessToken = session?.access_token || null;
  } catch {
    accessToken = null;
  }
  return new SafApi({
    url,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    accessToken,
    auth,
  });
}
